<template>
    <app-layout v-if="isPageReady && isReferralMade" printClass="print-referral-summary">
        <common-content-spacer height="small">
            <common-page-header :heading="$t('pages.referrals.new.steps.confirmation.request_sent')"></common-page-header>

            <common-page-plain :hasLargePadding="true">
                <referrals-confirmation :heading="$t('pages.referrals.new.steps.confirmation.heading')" type="success">
                    <template #content>
                        <common-summary>
                            <common-summary-item :label="$t('pages.referrals.new.steps.confirmation.summary.patient_name')">
                                <common-user-name :user="referral.patient"></common-user-name>
                            </common-summary-item>

                            <common-summary-item v-if="selectedSlot">
                                <common-date :date="selectedSlot.date" :copy="selectedSlot.date.format('dddd')">
                                    <template #time>
                                        <common-slots :slots="[selectedSlot.slot]"></common-slots>
                                    </template>
                                </common-date>
                            </common-summary-item>

                            <common-summary-item :label="$t('pages.referrals.new.steps.confirmation.summary.diagnosis')">
                                {{ referral.diagnosis }}
                            </common-summary-item>

                            <common-summary-item
                                :label="$t('pages.referrals.new.steps.confirmation.summary.to')"
                                :isInline="true"
                            >
                                <common-user-name :user="physician"></common-user-name>

                                <br />

                                <common-location-address v-if="location" :location="location"></common-location-address>

                                <p v-if="physician.phone">
                                    {{ $t('pages.referrals.new.steps.confirmation.summary.tel', { phone: physician.phone }) }}
                                </p>
                            </common-summary-item>
                        </common-summary>
                    </template>

                    <template #footer>
                        <common-buttons>
                            <common-button
                                :label="$t('pages.referrals.new.steps.confirmation.summary.print')"
                                :isSubmittable="false"
                                type="primary"
                                @click="printSummary()"
                            ></common-button>
                        </common-buttons>
                    </template>
                </referrals-confirmation>
            </common-page-plain>
        </common-content-spacer>
    </app-layout>

    <app-layout v-else-if="isPageReady">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>

                <common-page-header :heading="pageHeading">
                    <template #right>
                        <referrals-new-back
                            :route="{
                                name: 'referrals.new.results',
                                query: $route.params.originalQuery || $route.query,
                                params: { existingReferral: $route.params.existingReferral },
                            }"
                            :results="[physician]"
                        ></referrals-new-back>
                    </template>
                </common-page-header>
            </common-content-spacer>

            <common-page-split :showAside="!isOutsider">
                <template #aside>
                    <common-content-spacer height="medium">
                        <practice-member-card
                            v-if="$route.query.practiceType === PracticeTypeEnum.PHYSICIAN"
                            :physician="physician"
                            :isStandalone="false"
                            :showNetworkInclusionStatus="false"
                        ></practice-member-card>
                        <practice-card
                            v-if="$route.query.practiceType === PracticeTypeEnum.ANCILLARY"
                            :practice="ancillaryPractice"
                            :isStandalone="false"
                        ></practice-card>
                        <common-content-group>
                            <common-content-spacer height="medium">
                                <common-icon-info v-if="location" icon="location" size="huge">
                                    <common-location-address
                                        :location="location"
                                        :isNameDisplayed="true"
                                    ></common-location-address>
                                </common-icon-info>

                                <common-icon-info :href="`mailto:${physician.email}`" icon="email" size="huge">
                                    {{ physician.email || ancillaryPractice.email }}
                                </common-icon-info>
                            </common-content-spacer>
                        </common-content-group>
                    </common-content-spacer>
                </template>
                <template #main>
                    <template v-if="!showSlotsForFollowUpVisit">
                        <common-content-wrapper width="large">
                            <common-content-spacer height="large">
                                <referrals-new-make-form
                                    :selectedSlot="selectedSlot?.slot"
                                    :practice="practice"
                                    :location="location"
                                    @startOver="selectSlot(null)"
                                    @sent="markReferralAsMade($event)"
                                ></referrals-new-make-form>
                            </common-content-spacer>
                        </common-content-wrapper>
                    </template>
                    <template v-else>
                        <common-content-wrapper width="gigantic">
                            <div class="propose-time-calendar follow-up-calendar">
                                <availability-topnav-content
                                    :currentDate="calendarDate"
                                    :view="calendarView"
                                    :onDateChange="changeCalendarDate"
                                    :showLocationSelector="false"
                                    :showManageButton="false"
                                    :showViewToggle="false"
                                    :maxSlots="1"
                                ></availability-topnav-content>

                                <div class="propose-time-calendar__send">
                                    <common-button
                                        :label="$t('pages.referrals.pool.send_to_patient_provider')"
                                        :isSubmittable="false"
                                        :isDisabled="storeSelectedSlots.length < 1"
                                        size="medium"
                                        type="primary"
                                        :isProcessing="isProcessingFollowUp"
                                        @click="confirmFollowUpSlot()"
                                    ></common-button>
                                </div>

                                <availability-schedule
                                    v-if="scheduleUser.id && schedulePractice.id"
                                    :user="scheduleUser"
                                    :practice="schedulePractice"
                                    :practiceLocationId="practiceLocationId"
                                    :currentDate="calendarDate"
                                ></availability-schedule>
                            </div>
                        </common-content-wrapper>
                    </template>
                </template>
            </common-page-split>
        </common-content-spacer>
    </app-layout>
</template>

<script>
    import { mapGetters, mapMutations } from 'vuex'
    import { importComponents } from '~/helpers'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import ReferralsNewPageMixin from 'mixins/ReferralsNewPageMixin'
    import AppLayout from 'components/layouts/AppLayout'
    import { composeDisplayName } from 'components/common/CommonUserName'
    import PhysiciansAPI from 'api/PhysiciansAPI'
    import PracticesAPI from 'api/PracticesAPI'
    import PatientAPI from 'api/PatientAPI'
    import ReferralsAPI from 'api/ReferralsAPI'
    import DiagnosisAPI from 'api/DiagnosisAPI'
    import ReferralsNewMakeForm from 'components/forms/ReferralsNewMakeForm'
    import PracticeMemberCard from 'components/page-elements/practice/PracticeMemberCard'
    import PracticeCard from 'components/page-elements/practice/PracticeCard'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'
    import UserTypeEnum from '@/enums/UserTypeEnum'
    import dayjs from 'dayjs'
    import AvailabilitySchedule from 'components/page-elements/AvailabilitySchedule'
    import AvailabilityTopnavContent from 'components/layouts/app-layout/AvailabilityTopnavContent'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin, ReferralsNewPageMixin],

        components: {
            AppLayout,
            ReferralsNewMakeForm,
            PracticeMemberCard,
            PracticeCard,
            AvailabilitySchedule,
            AvailabilityTopnavContent,
            ...importComponents(require.context('components/form-elements')),
        },

        provide() {
            return {
                isReferralPool: false,
                sharedState: null,
            }
        },

        data() {
            return {
                physician: {},
                ancillaryPractice: {},
                selectedSlot: null,
                isReferralMade: false,
                PracticeTypeEnum: PracticeTypeEnum,
                isOutsider: false,
                outsider: {},
                isProcessingFollowUp: false,
            }
        },

        loadData(self) {
            if (self.$route.query.practiceType === PracticeTypeEnum.ANCILLARY && !self.$route.query.outsider) {
                return {
                    ancillaryPractice: PracticesAPI.getPracticeById(self.$route.query.practiceId),
                }
            } else {
                if (!self.$route.query.outsider) {
                    return {
                        physician: PhysiciansAPI.getPhysicianData({
                            userId: self.$auth.getActiveMember()?.id ? self.$auth.getActiveMember()?.id : self.$auth.getUser().id,
                            physicianId: self.$route.query.physicianId,
                        }),
                    }
                } else {
                    return {
                        physician: {},
                    }
                }
            }
        },

        computed: {
            ...mapGetters('availability', { storeSelectedSlots: 'selectedSlots' }),

            pageHeading() {
                if (this.$route.query.outsider) {
                    return this.$t('pages.referrals.new.title')
                }
                const practiceType = this.$route.query.practiceType
                return this.$t(`pages.referrals.new.steps.schedule.heading.${practiceType}`, {
                    name:
                        this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY
                            ? this.ancillaryPractice.name
                            : this.physician?.designation
                            ? `${composeDisplayName(this.physician)}, ${this.physician.designation}`
                            : composeDisplayName(this.physician),
                })
            },

            isSlotSelected() {
                return this.selectedSlot
            },

            showSlotsForFollowUpVisit() {
                return this.$route.query.followUpVisit
            },

            calendarDate() {
                return this.$route.query.date ? dayjs(this.$route.query.date).toDate() : new Date()
            },

            calendarView() {
                return this.$route.query.view || 'monthly'
            },

            practiceLocationId() {
                return Number(this.$route.query.practiceLocationId) || 0
            },

            scheduleUser() {
                return this.physician && this.physician.id
                    ? this.physician
                    : {
                          id:
                              Number(this.$route.query.physicianId || this.$route.query.userId) ||
                              this.$auth.getActiveMember()?.id ||
                              this.$auth.getUser()?.id,
                      }
            },

            practice() {
                if (this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY) {
                    return this.ancillaryPractice && this.ancillaryPractice.id
                        ? this.ancillaryPractice
                        : { id: Number(this.$route.query.practiceId) || 0 }
                } else {
                    return (
                        this.$get(this.physician, UserTypeEnum.PRACTICE) ||
                        this.physician?.practice || { id: Number(this.$route.query.practiceId) || 0 }
                    )
                }
            },

            schedulePractice() {
                if (this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY) {
                    return this.ancillaryPractice && this.ancillaryPractice.id
                        ? this.ancillaryPractice
                        : { id: Number(this.$route.query.practiceId) || this.$auth.getPractice()?.id }
                }
                const nestedPractice =
                    this.practice || this.$get(this.physician, UserTypeEnum.PRACTICE) || this.physician?.practice
                if (nestedPractice && nestedPractice.id) {
                    return nestedPractice
                }
                return { id: Number(this.$route.query.practiceId) || this.$auth.getPractice()?.id }
            },

            location() {
                if (this.$route.query.practiceType === PracticeTypeEnum.ANCILLARY && !this.$route.query.outsider) {
                    const mainLocation = {
                        address: this.ancillaryPractice.address,
                        city: this.ancillaryPractice.city,
                        state: this.ancillaryPractice.state,
                        zip: this.ancillaryPractice.zip,
                        isAvailabilitySet: this.ancillaryPractice.isAvailabilitySet,
                        id: 0,
                    }
                    const locations = [mainLocation, ...(this.ancillaryPractice.locations || [])]
                    return locations.find((item) => item.id === parseInt(this.$route.query.practiceLocationId))
                } else {
                    if (!this.practice) return null
                    if (this.$route.query.practiceLocationId === '0') {
                        return this.practice
                    } else {
                        return this.practice.locations?.find(({ id }) => id === parseInt(this.$route.query.practiceLocationId))
                    }
                }
            },
        },

        methods: {
            ...mapMutations('availability', ['SET_SELECT_MODE', 'SET_MAX_SLOTS']),

            changeCalendarDate(date) {
                this.$router
                    .push({
                        name: this.$route.name,
                        params: this.$route.params,
                        query: {
                            ...this.$route.query,
                            date: dayjs(date).format('YYYY-MM-DD'),
                        },
                    })
                    .catch(() => null)
            },

            confirmFollowUpSlot() {
                const slot = this.storeSelectedSlots[0]
                if (!slot) return

                this.isProcessingFollowUp = true

                const fromUserId = parseInt(this.$auth.getActiveMember()?.id || this.$auth.getUser()?.id)
                const toUserId = parseInt(this.$route.query.physicianId || this.physician?.id)
                const insurancePlanId = parseInt(this.$route.query.insurancePlanId) || null
                const subSpecialityId = parseInt(this.$route.query.subSpecialityId) || null
                const toPracticeId = parseInt(this.$route.query.practiceId || this.practice?.id)
                const toPracticeLocationId = parseInt(this.$route.query.practiceLocationId || this.location?.id || 0)

                const payload = {
                    fromUserId,
                    toUserId,
                    insurancePlanId,
                    subSpecialityId,
                    date: slot.date,
                    startTime: slot.startTime,
                    endTime: slot.endTime,
                    toPracticeId,
                    toPracticeLocationId,
                    isInsuranceAuthorizationRequired: false,
                    isPrimaryCareOutsideMedmatch: false,
                    followUpReferral: Number(this.$route.query.referralId),
                    patient: {},
                }

                let preparePayloadPromise = Promise.resolve()

                if (this.$route.query.patientId) {
                    preparePayloadPromise = Promise.all([
                        PatientAPI.getPatientDetails(this.$route.query.patientId, this.$auth.getUser().id),
                        ReferralsAPI.get(
                            this.$route.query.referralId,
                            this.$auth.getActiveMember()?.id || this.$auth.getUser().id,
                        ),
                    ])
                        .then(([patientRes, referralRes]) => {
                            if (patientRes) {
                                payload.insuranceGroupNumber =
                                    patientRes?.mcard?.groupNumber === 'N/A' ? null : patientRes?.mcard?.groupNumber
                                payload.insuranceNumber =
                                    patientRes?.mcard?.memberId === 'N/A' ? null : patientRes?.mcard?.memberId
                                payload.patient = {
                                    firstName: patientRes?.firstName,
                                    lastName: patientRes?.lastName,
                                    dateOfBirth: patientRes?.dateOfBirth,
                                    mobilePhone: patientRes?.mobilePhone,
                                    address: patientRes?.address,
                                    city: patientRes?.city,
                                    state: patientRes?.state,
                                    zip: patientRes?.zip,
                                    floorNumber: patientRes?.floorNumber,
                                    apartmentNumber: patientRes?.apartmentNumber,
                                    buildingNumber: patientRes?.buildingNumber,
                                    ssn: patientRes?.ssn,
                                }
                            }

                            if (referralRes?.diagnosis) {
                                payload.diagnosis = referralRes.diagnosis
                                return DiagnosisAPI.getFromICD(referralRes.diagnosis, 'name')
                                    .then((icdResponse) => {
                                        if (icdResponse && icdResponse[0]?.data?.id) {
                                            payload.icdCodeId = icdResponse[0].data.id
                                        }
                                    })
                                    .catch(() => null)
                            }
                        })
                        .catch(() => null)
                }

                const parseTimeToMinutes = (timeStr) => {
                    if (typeof timeStr === 'number') return timeStr
                    if (!timeStr) return 0
                    const [h, m] = timeStr.split(':').map(Number)
                    return (h || 0) * 60 + (m || 0)
                }

                const fromMinutes = slot.from !== undefined ? parseTimeToMinutes(slot.from) : parseTimeToMinutes(slot.startTime)
                const toMinutes =
                    slot.to !== undefined
                        ? parseTimeToMinutes(slot.to)
                        : slot.endTime
                        ? parseTimeToMinutes(slot.endTime)
                        : fromMinutes + 60

                return preparePayloadPromise
                    .then(() => ReferralsAPI.create(payload))
                    .then((createdReferral) => {
                        this.selectedSlot = {
                            date: dayjs(slot.date),
                            slot: {
                                from: fromMinutes,
                                to: toMinutes,
                                startTime: slot.startTime,
                                endTime: slot.endTime,
                                date: slot.date,
                            },
                        }
                        this.markReferralAsMade(createdReferral || payload)
                    })
                    .catch(() => null)
                    .finally(() => {
                        this.isProcessingFollowUp = false
                    })
            },

            selectSlot(slot) {
                this.selectedSlot = slot
                this.markReferralAsMade
            },

            markReferralAsMade(referral) {
                if (referral.physician) {
                    this.physician = referral.physician
                }

                this.referral = referral
                this.isReferralMade = true
            },

            printSummary() {
                window.print()
            },

            resetPageData() {
                this.isReferralMade = false
                this.selectedSlot = null
            },
        },

        watch: {
            '$route.query'() {
                this.resetPageData()
            },

            showSlotsForFollowUpVisit: {
                immediate: true,
                handler(val) {
                    if (val) {
                        this.SET_MAX_SLOTS(1)
                    }
                    this.SET_SELECT_MODE(!!val)
                },
            },
        },

        mounted() {
            if (this.$route.query.outsider) {
                this.isOutsider = true
                this.outsider = {
                    firstName: this.$route.query.firstName,
                    lastName: this.$route.query.lastName,
                    email: this.$route.query.email,
                }
            }
            if (this.$route.query.selfReferred) {
                this.selectedSlot = {
                    date: dayjs(this.$route.query.date),
                    slot: {
                        startTime: this.$route.query.startTime,
                        endTime: this.$route.query.endTime,
                        date: this.$route.query.date,
                        from: dayjs(this.$route.query.startTime, 'HH:mm').diff(dayjs().startOf('day'), 'minutes'),
                        to: dayjs(this.$route.query.endTime, 'HH:mm').diff(dayjs().startOf('day'), 'minutes'),
                    },
                }
            }
        },

        beforeDestroy() {
            this.SET_SELECT_MODE(false)
            this.SET_MAX_SLOTS(3)
        },
    }
</script>
